import { type VariantProps, cva } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";

export const progressPieVariants = cva("flex items-center space-x-2", {
  variants: {
    variant: {
      pie: "",
      ring: "",
    },
    size: {
      default: "h-5.5",
      medium: "h-5.5",
    },
  },
  defaultVariants: {
    variant: "pie",
    size: "default",
  },
});

const circleBackgroundVariants = cva("", {
  variants: {
    variant: {
      pie: "text-gray-300",
      ring: "text-gray-400",
    },
  },
  defaultVariants: {
    variant: "pie",
  },
});

const sizeDimensions = {
  default: {
    size: 24,
    radius: {
      pie: 5,
      ring: 9,
    },
    strokeWidth: {
      pie: 10,
      ring: 3,
    },
  },
  medium: {
    size: 48,
    radius: {
      pie: 10,
      ring: 18,
    },
    strokeWidth: {
      pie: 20,
      ring: 6,
    },
  },
};

export interface ProgressPieProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof progressPieVariants> {
  value: number;
  showLabel?: boolean;
  colorClassName?: string;
}

type ProgressPieWithRefProps = ProgressPieProps & {
  ref?: React.Ref<HTMLDivElement>;
};
export const ProgressPie = ({
  value,
  variant = "pie",
  size = "default",
  showLabel = true,
  colorClassName = "text-primary",
  className,
  ref,
  ...props
}: ProgressPieWithRefProps) => {
  const progress = Math.max(0, Math.min(value, 100));
  const dimensions = sizeDimensions[size || "default"];
  const svgSize = dimensions.size;
  const radius = dimensions.radius[variant || "pie"];
  const strokeWidth = dimensions.strokeWidth[variant || "pie"];
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progress / 100) * circumference;
  const { t } = useTranslation();
  return (
    <div
      ref={ref}
      {...props}
      className={cn(progressPieVariants({ variant, size }), className)}
    >
      <div className="relative flex-none">
        {/* Container for SVG */}
        <svg
          width={svgSize}
          height={svgSize}
          viewBox={`0 0 ${svgSize} ${svgSize}`}
          className={colorClassName}
        >
          <title>{t("PROGRESS_PIE")}</title>
          <circle
            className={cn(circleBackgroundVariants({ variant }))}
            stroke="currentColor"
            fill={variant === "pie" ? "currentColor" : "none"}
            strokeWidth={strokeWidth}
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
          />
          <circle
            stroke="currentColor"
            fill="none"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            strokeLinecap="butt"
            transform={`rotate(-90 ${svgSize / 2} ${svgSize / 2})`}
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            className="transition-all"
          />
        </svg>
      </div>
      {showLabel && (
        <span className="text-gray-700 font-base text-sm tracking-[0.42px] whitespace-nowrap">
          {progress}%
        </span>
      )}
    </div>
  );
};
ProgressPie.displayName = "ProgressPie";
